/* eslint-disable complexity */
import { Box, Grid, Paper, styled } from "@mui/material";
import { Alert, paletteVar, Typography } from "@platform/design-system";
import { useSignedInUser } from "@platform/user/client";
import { PolicyDetails } from "@platform/view-policy-components";
import { useEffect, useState } from "react";

import { submitEvent } from "../../utils/tracking";
import { usePostApi } from "../../hooks";
import usePolicyData from "../../hooks/usePolicyData";
import { sortProductsByCoverage } from "../../pages/RFP/utils";
import {
  filterProductDataBasedOnFlag,
  formatCurrencyWithRound,
  mapLocToPolicyDetailsVariant,
  extractErrorMessage,
} from "../../utils";
import ConnectedCarrierBanner from "../connectedCarrierBanner";

import AddCurrentCoveragesModal from "./addCurrentCoveragesModal";
import AddCurrentCoverageTable from "./addCurrentCoverageTable";
import CoverageSelectionDropdown from "./coverageSelectionDropdown";
import PolicyNotFoundDialog from "./policyNotFoundDialog";

// TODO(eslint): reduce complexity

const AddCurrentCoverages = ({
  coverages,
  brokerHasMultipleStrategicTiers,
  onAddCoverages,
  tableHeight = "40vh",
  clientId,
  brokerType,
  hideNonNACols,
  showConnectedCarriers,
  embeded,
  showTitle = true,
  isAdmin = false,
  isAnalyst = false,
  clientType,
  client,
  isConsultant,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedCoverage, setSelectedCoverage] = useState("");
  const [coverageElem, setCoverageElem] = useState(null);
  const [policyDetailsConfig, setPolicyDetailsConfig] = useState(null);
  const { givenName, familyName, role, email } = useSignedInUser();
  const StyledGridItem = styled(Grid)(() => ({
    marginBottom: "1.5rem",
  }));
  const totalPremium = coverages?.reduce(
    (acc, { annualizedPremium }) => acc + annualizedPremium,
    0,
  );
  const { error, data } = usePostApi(
    "/brokerportal/v1/prepareCarriersAndCoverages",
    {},
  );
  let responseData;
  if (data) {
    responseData = filterProductDataBasedOnFlag(data);
  }
  const addCoverage = (value) => {
    setSelectedCoverage(value);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCoverage("");
    setOpenModal(false);
  };

  const isConnectedCarriers =
    showConnectedCarriers &&
    coverages.some(
      (c) =>
        c.isCarrierEnabled && !!mapLocToPolicyDetailsVariant(c.lineOfCoverage),
    );

  const showAddCoverageButton =
    !isAdmin &&
    !isAnalyst &&
    !isConsultant &&
    (brokerType !== "OneDigital" || clientType === "prospect_client");

  const {
    isDialogOpen,
    data: policyData,
    isLoading,
    error: policyError,
    openDialog,
    closeDialog,
    fetchData,
  } = usePolicyData(setCoverageElem, setPolicyDetailsConfig);

  useEffect(() => {
    if (!coverageElem) return;
    openDialog(
      coverageElem.brokerCarrierInfo.carrierId,
      coverageElem.groupNumber,
      coverageElem.lineOfCoverage,
      coverageElem.carrier,
    );
  }, [coverageElem, openDialog]);

  const canOpenPolicyDetails = isDialogOpen && !isLoading && policyData;
  const showPolicyNotFoundError = isDialogOpen && !isLoading && policyError;

  const sortedCoverages = sortProductsByCoverage(coverages, "coverage");

  const onCloseDialog = () => {
    handleMonitoringEvent(false);
    closeDialog();
  };
  const handleMonitoringEvent = (isOpen, coverage) => {
    const eventName = isOpen
      ? "Policy Details Button Clicked"
      : "Policy Details Closed";
    const coverageData = coverage ? coverage : coverageElem;
    submitEvent(eventName, {
      user_name: `${givenName} ${familyName}`,
      user_email_address: email,
      company_name: client.name,
      broker_name: client?.brokerName,
      user_role: role,
      carrier_name: coverageData.carrier,
      coverage: coverageData.coverage,
      group_number: coverageData.groupNumber,
    });
  };

  const handleOnSelectCoverage = (coverage) => {
    handleMonitoringEvent(true, coverage);
    setCoverageElem(coverage);
  };

  return (
    <>
      {openModal && (
        <AddCurrentCoveragesModal
          data={data}
          onAddCoverages={onAddCoverages}
          open={openModal}
          onClose={handleCloseModal}
          selectedCoverage={selectedCoverage}
          clientId={clientId}
        />
      )}
      {showPolicyNotFoundError ? (
        <PolicyNotFoundDialog
          onClose={onCloseDialog}
          isOpen={isDialogOpen}
          coverageName={coverageElem.lineOfCoverage}
          groupNumber={policyDetailsConfig.groupNumber}
        />
      ) : canOpenPolicyDetails ? (
        <>
          <PolicyDetails
            isOpen={isDialogOpen}
            onClose={onCloseDialog}
            data={policyData}
            variant={policyDetailsConfig.variant}
            carrierName={policyDetailsConfig.carrierName}
            onRefreshClick={() =>
              fetchData(
                coverageElem.brokerCarrierInfo.carrierId,
                coverageElem.groupNumber,
              )
            }
          />
        </>
      ) : null}
      <EmbededWrapper embeded={embeded}>
        <>
          {error && (
            <Box mb={2} width="inherit">
              <Alert severity="error">{extractErrorMessage(error)}</Alert>
            </Box>
          )}
          <StyledGridItem
            container
            direction="row"
            justifyContent={showTitle ? "space-between" : "end"}
            mb={2}
          >
            {showTitle && (
              <Typography
                variant="display-small"
                style={{
                  fontWeight: "400",
                  letterSpacing: "0px",
                }}
              >
                {" "}
                Current Coverage
              </Typography>
            )}
            {showAddCoverageButton && (
              <CoverageSelectionDropdown
                data={responseData}
                coverage={selectedCoverage}
                setCoverage={addCoverage}
              />
            )}
          </StyledGridItem>
          {isConnectedCarriers ? <ConnectedCarrierBanner /> : null}
          <Grid container component={Paper}>
            <AddCurrentCoverageTable
              coverages={sortedCoverages}
              brokerHasMultipleStrategicTiers={brokerHasMultipleStrategicTiers}
              tableHeight={tableHeight}
              hideNonNACols={hideNonNACols}
              isConnectedCarriers={isConnectedCarriers}
              onSelectCoverage={handleOnSelectCoverage}
              selectedCoverage={coverageElem}
              isLoading={isLoading}
            />
            {sortedCoverages.length ? (
              <StyledGridItem
                size={12}
                mt={3}
                sx={{ padding: "16px 24px !important" }}
                bgcolor={paletteVar("surfaces-neutral", "#fafafa")}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                  justifyContent="center"
                >
                  <Typography>
                    <strong>Total Premium</strong>
                  </Typography>
                  <Typography variant="body-medium">
                    {formatCurrencyWithRound(totalPremium)}
                  </Typography>
                </Box>
              </StyledGridItem>
            ) : null}
          </Grid>
        </>
      </EmbededWrapper>
    </>
  );
};

const EmbededWrapper = ({ children, embeded }) => {
  if (embeded) {
    return children;
  }
  return (
    <Grid container>
      <Grid container component={Paper} p={5}>
        {children}
      </Grid>
    </Grid>
  );
};

export default AddCurrentCoverages;
